import React, { useState } from 'react';
import { Form, Input, Upload, Button, message } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomModal from './CustomModal';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';

const { TextArea } = Input;

const AddPaymentModal = ({ isOpen, onClose, onSubmit, loading = false }) => {
  const [form] = Form.useForm();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [fileList, setFileList] = useState([]);

  const handleImageUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('You can only upload image files!');
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > 5) {
      message.error('Image must be smaller than 5MB!');
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    setImageFile(file);
    setFileList([file]);

    return Upload.LIST_IGNORE;
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFileList([]);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Build JSON body
      const body = {
        name: values.name,
        description: values.description,
        status: 0
      };

      if (imageFile) {
        const cloudinaryUrl = await uploadToCloudinary(imageFile);
        body.url = cloudinaryUrl; // add image URL
      }

      await onSubmit(body); // send JSON body

      form.resetFields();
      handleRemoveImage();
      onClose();
    } catch (error) {
      console.error('Form validation or upload failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    handleRemoveImage();
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Payment Method"
      subtitle="Configure a new payment option for your customers"
      primaryActionText="Add Payment Method"
      secondaryActionText="Cancel"
      onPrimaryAction={handleSubmit}
      onSecondaryAction={handleClose}
      primaryActionDisabled={loading}
      loading={loading}
      width={600}
      isCenter={true}
    >
      <div className="p-6">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="space-y-4"
        >
          <Form.Item
            name="name"
            label="Payment Method Name"
            rules={[
              { required: true, message: 'Please enter payment method name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 50, message: 'Name must not exceed 50 characters' }
            ]}
          >
            <Input 
              placeholder="e.g., FlutterWave, Paystack, Opay"
              size="large"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter description' },
              { min: 10, message: 'Description must be at least 10 characters' },
              { max: 200, message: 'Description must not exceed 200 characters' }
            ]}
          >
            <TextArea
              placeholder="Describe this payment method for your customers"
              rows={3}
              size="large"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item label="Payment Method Icon" className="mb-0">
            <div className="space-y-4">
              {imagePreview ? (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Payment method preview"
                    className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-white rounded-full shadow-md hover:bg-red-50"
                    size="small"
                  />
                </div>
              ) : (
                <Upload
                  beforeUpload={handleImageUpload}
                  onRemove={handleRemoveImage}
                  fileList={fileList}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    className="rounded-lg border-dashed border-2 border-gray-300 hover:border-orange-500 hover:text-orange-500"
                  >
                    Upload Icon
                  </Button>
                </Upload>
              )}
              <p className="text-xs text-gray-500">
                Upload a square icon for this payment method (max 5MB)
              </p>
            </div>
          </Form.Item>
        </Form>
      </div>
    </CustomModal>
  );
};

export default AddPaymentModal;
